import React, { useState, useEffect, useCallback, useRef } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MessageSquare, Users, Search, Send, Loader2, Image, FileText, Paperclip, X, Upload, UserCheck, Bot, Download, Play } from "lucide-react";

/**
 * Renders a media message inside a chat bubble. Uses the auth-gated backend proxy
 * `/api/inbox/media/{id}` so images/videos/audios/docs actually appear instead of the
 * "[media]" placeholder.
 */
function MessageMedia({ msg }) {
  const src = `${API}/inbox/media/${encodeURIComponent(msg.id || msg.wa_message_id || "")}`;
  const type = (msg.media_type || "").toLowerCase();
  const token = (typeof window !== "undefined") ? localStorage.getItem("wa_token") : "";
  const [blobSrc, setBlobSrc] = React.useState(null);

  React.useEffect(() => {
    // For image/video/audio elements the browser can't add Authorization headers on <img src>.
    // Fetch as blob with auth then hand a blob URL to the element.
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      if (!type || type === "text") return;
      try {
        const r = await fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobSrc(objectUrl);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [msg.id, msg.wa_message_id, type]);

  if (!blobSrc) {
    return (
      <div className="my-1 flex items-center gap-2 text-xs opacity-70" data-testid={`msg-media-loading-${msg.id || ""}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Cargando {type || "media"}…
      </div>
    );
  }
  if (type === "image" || type === "sticker") {
    return (
      <a href={blobSrc} target="_blank" rel="noreferrer" data-testid={`msg-media-image-${msg.id || ""}`}>
        <img src={blobSrc} alt={msg.file_name || "image"} className="mt-1 mb-1 rounded max-w-full max-h-72 cursor-pointer object-cover" />
      </a>
    );
  }
  if (type === "video") {
    return (
      <video src={blobSrc} controls playsInline className="mt-1 mb-1 rounded max-w-full max-h-72 bg-black" data-testid={`msg-media-video-${msg.id || ""}`}>
        Tu navegador no soporta video.
      </video>
    );
  }
  if (type === "audio") {
    return (
      <audio src={blobSrc} controls className="mt-1 mb-1 max-w-[240px]" data-testid={`msg-media-audio-${msg.id || ""}`}>
        Tu navegador no soporta audio.
      </audio>
    );
  }
  // Document / unknown
  return (
    <a href={blobSrc} download={msg.file_name || "attachment"} className="mt-1 mb-1 flex items-center gap-2 text-xs underline" data-testid={`msg-media-doc-${msg.id || ""}`}>
      <FileText className="w-4 h-4" /> {msg.file_name || "Descargar archivo"}
      {msg.file_size ? <span className="opacity-70">({Math.round(msg.file_size / 1024)} KB)</span> : null}
    </a>
  );
}

export default function InboxPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const [sendType, setSendType] = useState("text");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [dragOverComposer, setDragOverComposer] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [takeover, setTakeover] = useState(false);
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const msgsEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/inbox/conversations`);
      setConversations(data);
    } catch (err) { console.error("Failed to load conversations:", err); }
    finally { setLoadingConvs(false); }
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Auto-refresh messages when a conversation is selected
  useEffect(() => {
    if (!selectedConv) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await authAxios.get(`${API}/inbox/messages/${encodeURIComponent(selectedConv.id)}`);
        setMessages(data);
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(interval);
  }, [selectedConv]);

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    setLoadingMsgs(true);
    setComposerText("");
    setMediaUrl("");
    setSendType("text");
    setAttachedFile(null);
    try {
      const { data } = await authAxios.get(`${API}/inbox/messages/${encodeURIComponent(conv.id)}`);
      setMessages(data);
    } catch (err) { console.error("Failed to load messages:", err); }
    finally { setLoadingMsgs(false); }
    try {
      const { data: tk } = await authAxios.get(`${API}/takeover/${encodeURIComponent(conv.id)}`);
      setTakeover(!!tk.enabled);
    } catch { setTakeover(false); }
  };

  const toggleTakeover = async () => {
    if (!selectedConv) return;
    setTakeoverLoading(true);
    try {
      const next = !takeover;
      await authAxios.post(`${API}/takeover`, {
        conversation_id: selectedConv.id,
        enabled: next,
        note: "Toggled from Inbox",
      });
      setTakeover(next);
    } finally { setTakeoverLoading(false); }
  };

  useEffect(() => {
    if (msgsEndRef.current) msgsEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    setUploadError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await authAxios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMediaUrl(data.url);
      setSendType(data.file_type);
      setAttachedFile({ name: file.name, type: data.file_type, url: data.url });
    } catch (err) {
      const errMsg = err.response?.data?.detail || "File upload failed";
      setUploadError(errMsg);
      setTimeout(() => setUploadError(""), 5000);
    } finally {
      setUploadingFile(false);
      // Reset the file input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleComposerDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverComposer(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleComposerDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverComposer(true);
  };

  const handleComposerDragLeave = (e) => {
    e.preventDefault();
    setDragOverComposer(false);
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    setMediaUrl("");
    setSendType("text");
    setUploadError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    if ((!composerText.trim() && !mediaUrl.trim()) || !selectedConv) return;
    setSending(true);
    try {
      await authAxios.post(`${API}/inbox/send`, {
        conversation_id: selectedConv.id,
        content: composerText,
        message_type: sendType,
        media_url: mediaUrl,
        caption: sendType !== "text" ? composerText : "",
      });
      setComposerText("");
      setMediaUrl("");
      setSendType("text");
      setAttachedFile(null);
      const { data } = await authAxios.get(`${API}/inbox/messages/${encodeURIComponent(selectedConv.id)}`);
      setMessages(data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send message");
    } finally { setSending(false); }
  };

  const filtered = conversations.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 h-[calc(100vh-0px)]" data-testid="inbox-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#0f172a]">Inbox</h1>
        <p className="text-sm text-muted-foreground mt-1">View and manage your WhatsApp conversations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100%-80px)]">
        {/* Conversation List */}
        <Card className="lg:col-span-1 flex flex-col" data-testid="conversation-list">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search conversations..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-conversations" />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-[calc(100vh-280px)]">
              {loadingConvs ? (
                <div className="p-4 space-y-3">{[1,2,3].map(i => (<div key={i} className="animate-pulse flex gap-3 p-2"><div className="w-10 h-10 bg-slate-200 rounded-full" /><div className="flex-1 space-y-2"><div className="h-4 bg-slate-200 rounded w-3/4" /><div className="h-3 bg-slate-200 rounded w-1/2" /></div></div>))}</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground px-4">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{conversations.length === 0 ? "No conversations yet" : "No matching conversations"}</p>
                </div>
              ) : (
                <div>
                  {filtered.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 transition-colors ${selectedConv?.id === conv.id ? "bg-slate-100" : ""}`}
                      data-testid={`conversation-item-${conv.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          {conv.is_group ? <Users className="w-5 h-5 text-slate-500" /> : <MessageSquare className="w-5 h-5 text-slate-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 justify-between">
                            <p className="text-sm font-medium truncate">{conv.name}</p>
                            <Badge variant="outline" className="text-[10px] shrink-0">{conv.is_group ? "Group" : "PM"}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.last_message}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Message Thread */}
        <Card className="lg:col-span-2 flex flex-col" data-testid="message-thread">
          {!selectedConv ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Select a conversation to view messages</p>
              </div>
            </div>
          ) : (
            <>
              <CardHeader className="border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    {selectedConv.is_group ? <Users className="w-5 h-5 text-slate-500" /> : <MessageSquare className="w-5 h-5 text-slate-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base" data-testid="selected-conversation-name">{selectedConv.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{selectedConv.is_group ? "Group chat" : "Private chat"}</p>
                  </div>
                  <Button
                    variant={takeover ? "default" : "outline"}
                    size="sm"
                    onClick={toggleTakeover}
                    disabled={takeoverLoading}
                    className={takeover ? "bg-amber-500 hover:bg-amber-600" : ""}
                    data-testid="takeover-toggle"
                  >
                    {takeover ? <><UserCheck className="w-4 h-4 mr-1" /> Humano activo</> : <><Bot className="w-4 h-4 mr-1" /> Pausar bot</>}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[calc(100vh-340px)] p-4">
                  {loadingMsgs ? (
                    <div className="space-y-3">{[1,2,3].map(i => (<div key={i} className="animate-pulse space-y-1"><div className="h-3 bg-slate-200 rounded w-24" /><div className="h-4 bg-slate-200 rounded w-3/4" /></div>))}</div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground"><p className="text-sm">No messages in this conversation</p></div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg, i) => (
                        <div key={msg.id || i} className={`flex flex-col ${msg.is_from_bot ? "items-end" : "items-start"}`} data-testid={`message-${i}`}>
                          <div className={`max-w-[75%] rounded-xl px-3 py-2 ${msg.is_from_bot ? "bg-[#0f172a] text-white" : "bg-slate-100 text-[#0f172a]"}`}>
                            <p className="text-xs font-medium text-[#22c55e] mb-0.5">{msg.is_from_bot ? (msg.sender === "Bot" ? "Bot" : "You") : msg.sender}</p>
                            {msg.media_url && msg.media_type && msg.media_type !== "text" && (
                              <MessageMedia msg={msg} />
                            )}
                            {msg.content && !/^\[(image|video|audio|document|sticker|media)\]$/i.test(msg.content) && (
                              <p className="text-sm break-words whitespace-pre-wrap">{msg.content}</p>
                            )}
                          </div>
                          <span className="text-[10px] text-muted-foreground mt-1 px-1">{new Date(msg.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                      <div ref={msgsEndRef} />
                    </div>
                  )}
                </ScrollArea>
              </CardContent>

              {/* Composer */}
              <div
                className={`border-t p-3 transition-colors ${dragOverComposer ? "bg-green-50 border-t-[#22c55e]" : ""}`}
                data-testid="message-composer"
                onDrop={handleComposerDrop}
                onDragOver={handleComposerDragOver}
                onDragLeave={handleComposerDragLeave}
              >
                {/* Drag & drop overlay */}
                {dragOverComposer && (
                  <div className="flex items-center gap-2 mb-2 p-3 border-2 border-dashed border-[#22c55e] rounded-lg text-center">
                    <Upload className="w-4 h-4 text-[#22c55e]" />
                    <span className="text-sm text-[#22c55e] font-medium">Drop file here to upload</span>
                  </div>
                )}

                {/* Upload error */}
                {uploadError && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-xs text-red-600">{uploadError}</span>
                    <Button variant="ghost" size="icon" className="h-5 w-5 ml-auto" onClick={() => setUploadError("")}><X className="w-3 h-3" /></Button>
                  </div>
                )}

                {/* Uploading indicator */}
                {uploadingFile && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                    <span className="text-xs text-blue-600">Uploading file to Cloudinary...</span>
                  </div>
                )}

                {/* Attached File Preview */}
                {attachedFile && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-lg border">
                    <Paperclip className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium flex-1 truncate">{attachedFile.name}</span>
                    <Badge variant="outline" className="text-[10px]">{attachedFile.type}</Badge>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={removeAttachment} data-testid="remove-attachment"><X className="w-3 h-3" /></Button>
                  </div>
                )}

                {/* Manual media URL (when no file attached) */}
                {sendType !== "text" && !attachedFile && (
                  <div className="flex gap-2 mb-2">
                    <Input value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="Media URL..." className="text-sm flex-1" data-testid="media-url-input" />
                  </div>
                )}

                <div className="flex gap-2 items-center">
                  <div className="flex gap-1">
                    <Button variant={sendType === "text" && !attachedFile ? "default" : "ghost"} size="icon" className={`h-8 w-8 ${sendType === "text" && !attachedFile ? "bg-[#0f172a]" : ""}`} onClick={() => { setSendType("text"); removeAttachment(); }} data-testid="send-type-text"><MessageSquare className="w-3.5 h-3.5" /></Button>
                    <Button variant={sendType === "image" && !attachedFile ? "default" : "ghost"} size="icon" className={`h-8 w-8 ${sendType === "image" && !attachedFile ? "bg-[#0f172a]" : ""}`} onClick={() => setSendType("image")} data-testid="send-type-image"><Image className="w-3.5 h-3.5" /></Button>
                    <Button variant={sendType === "document" && !attachedFile ? "default" : "ghost"} size="icon" className={`h-8 w-8 ${sendType === "document" && !attachedFile ? "bg-[#0f172a]" : ""}`} onClick={() => setSendType("document")} data-testid="send-type-doc"><FileText className="w-3.5 h-3.5" /></Button>
                    {/* Cloudinary File Upload */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      data-testid="attach-file-btn"
                    >
                      {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
                    onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                    data-testid="composer-file-input"
                  />
                  <Input
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    placeholder={attachedFile ? "Caption (optional)..." : sendType === "text" ? "Type a message..." : "Caption (optional)..."}
                    className="flex-1 text-sm"
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                    data-testid="composer-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={sending || (!composerText.trim() && !mediaUrl.trim())}
                    className="bg-[#22c55e] hover:bg-[#16a34a] text-white"
                    data-testid="send-button"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
